import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { HeightReveal } from '@/components/height-reveal';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import useGetWindowWidth from '../utils/controllers/useGetWindowWidth';
import GlobalSidebar from './GlobalSidebar';
import PendingTeamInvitesBar from './PendingTeamInvitesBar';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetTeamAccessableSectionsQuery,
  useUserConfigurationQuery,
  useUserInfoQuery,
} from '../services/api';
import {
  setIsAccessibleSectionLoaded,
  setTeamAccessibleSection,
} from '../redux/slices/team/teamSlice';
import { setUserData } from '../redux/slices/user/profileSlice';
import { setIframeLanguage, setIframePublisher } from '../redux/slices/iframe/iframeSlice';
import SchedulingForm from '../utils/components/SchedulingForm';
import CommandPalette from '../utils/components/commandPalette/CommandPalette';
import TourHost from '../components/tour/TourHost';
import GlobalSupportDrawer from '../utils/components/GlobalSupportDrawer';
import GlobalHelpChat from '../utils/components/GlobalHelpChat';
import { useLocation } from 'react-router-dom';
import { setHasCreditAccess } from '../redux/slices/appSlice';
import useSetUserConfigData from '../utils/controllers/useSetUserConfigData';
import NotificationBanner from './NotificationBanner';
import ChatbotTriggerButton from '../utils/components/ChatbotTriggerButton';
import AppHeader from '../components/app/AppHeader';
import AppInset from '../components/app/AppInset';
import ThemeToggle from '../components/app/ThemeToggle';
import { applyBrandingCss } from '../utils/controllers/applyBrandingCss';

const DESKTOP_SIDEBAR_WIDTH = 248;
const COLLAPSED_SIDEBAR_WIDTH = 80;
const MOBILE_BREAKPOINT = 768;
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

function AuthPageLayout({ children, HeaderComp }) {
  useSetUserConfigData();
  const location = useLocation();
  const branding = useSelector(state => state.branding.branding);
  const dispatch = useDispatch();
  const { data, isSuccess } = useUserInfoQuery();
  const { isSuccess: accessableSectionsIsSuccess, data: accessableSections } =
    useGetTeamAccessableSectionsQuery();

  const notificationBanner = useSelector(state => state.app.notificationBanner);

  // please dont remove this API call
  const { data: userConfigData } = useUserConfigurationQuery();

  useEffect(() => {
    if (accessableSectionsIsSuccess) {
      dispatch(setTeamAccessibleSection(accessableSections?.data || []));
    }
  }, [accessableSectionsIsSuccess, accessableSections]);

  useEffect(() => {
    dispatch(setIsAccessibleSectionLoaded(accessableSectionsIsSuccess));
  }, [accessableSectionsIsSuccess]);

  useEffect(() => {
    if (data?.success) {
      dispatch(setUserData(data?.data));
      dispatch(
        setIframePublisher({
          name: `${data?.data?.firstname} ${data?.data?.lastname}`,
          logo: data?.data?.companyLogo,
        })
      );
      dispatch(setIframeLanguage(data?.data?.language));
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (userConfigData?.data?.lockedPortalSection?.includes('CreditUsage')) {
      dispatch(setHasCreditAccess(false));
    }
  }, [userConfigData?.data?.lockedPortalSection]);

  // Custom hook to get window width
  const width = useGetWindowWidth();
  const isMobile = width <= MOBILE_BREAKPOINT;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) {
      return true;
    }

    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });

  // Phones get the full sidebar in a slide-in sheet instead of a permanent icon rail.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? COLLAPSED_SIDEBAR_WIDTH
      : DESKTOP_SIDEBAR_WIDTH;

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, isMobile]);

  // Responsive collapse, same timing as the old sider breakpoint (max-width 767.98px):
  // runs once on mount and on every breakpoint change, before the isMobile effect below.
  const breakpointHandlerRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mql = window.matchMedia('screen and (max-width: 767.98px)');
    const handler = () => breakpointHandlerRef.current?.(mql.matches);
    mql.addEventListener('change', handler);
    handler();
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
      return;
    }

    setSidebarCollapsed(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  }, [isMobile]);

  const handleSidebarCollapse = collapsed => {
    setSidebarCollapsed(collapsed);

    if (!isMobile) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    }
  };

  const handleToggleSidebar = () => {
    handleSidebarCollapse(!sidebarCollapsed);
  };

  breakpointHandlerRef.current = broken => {
    if (broken) {
      setSidebarCollapsed(true);
    }
    if (sidebarCollapsed !== broken) {
      handleSidebarCollapse(broken);
    }
  };

  const isAiLabsEnginePage =
    location.pathname.includes('/aiLabs/create') ||
    location.pathname.includes('/aiLabs/edit') ||
    location.pathname.includes('/ailabs/create') ||
    location.pathname.includes('/ailabs/edit');

  useEffect(() => {
    applyBrandingCss(branding.primaryColor);
  }, [branding.primaryColor]);

  return (
    <div className="flex min-h-screen w-full flex-auto bg-background">
      {/* Sidebar */}
      {isMobile ? (
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent
            side="left"
            showCloseButton={false}
            className="w-[min(85vw,280px)] border-e-0 bg-sidebar p-0 text-sidebar-foreground"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Pages and settings</SheetDescription>
            <GlobalSidebar collapsed={false} showCollapseToggle={false} />
          </SheetContent>
        </Sheet>
      ) : (
        <aside
          className="fixed inset-y-0 start-0 z-40 h-screen overflow-hidden bg-sidebar transition-[width] duration-200 ease-out-quad"
          style={{ width: sidebarWidth }}
        >
          {/* The collapse toggle lives in the app header. */}
          <GlobalSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
            showCollapseToggle={false}
          />
        </aside>
      )}

      {/* Main Content */}
      <div
        className={cn(
          'flex w-0 min-w-0 flex-auto flex-col bg-sidebar transition-[margin-left] duration-200 ease-out-quad',
          isAiLabsEnginePage ? 'h-dvh max-h-dvh min-h-0 overflow-hidden' : 'min-h-screen'
        )}
        style={{ marginLeft: sidebarWidth }}
      >
        <AppInset fill={isAiLabsEnginePage}>
          <PendingTeamInvitesBar />
          <HeightReveal show={!!notificationBanner} initial={false}>
            <NotificationBanner />
          </HeightReveal>
          <AppHeader
            collapsed={sidebarCollapsed}
            onToggleSidebar={isMobile ? () => setMobileNavOpen(true) : handleToggleSidebar}
            mobile={isMobile}
            actions={
              <>
                <ChatbotTriggerButton />
                <ThemeToggle />
              </>
            }
          />
          {HeaderComp && (
            <header className="sticky top-14 z-10 shrink-0 border-b bg-background/85 px-4 py-3 backdrop-blur-[16px] backdrop-saturate-150 sm:px-6 sm:pt-4 sm:pb-3 md:px-10 md:pt-5 md:pb-4">
              <div className="mx-auto w-full max-w-[2000px] overflow-hidden">
                <HeaderComp />
              </div>
            </header>
          )}
          <div
            className={cn(
              'flex min-h-0 w-full min-w-0 flex-1 flex-wrap justify-center',
              isAiLabsEnginePage && 'overflow-hidden'
            )}
          >
            <main
              className={cn(
                'w-full max-w-[2000px] min-w-0 flex-auto overflow-x-clip',
                'motion-safe:[&>:first-child]:animate-in motion-safe:[&>:first-child]:fade-in motion-safe:[&>:first-child]:slide-in-from-bottom-1 motion-safe:[&>:first-child]:duration-500 motion-safe:[&>:first-child]:ease-out-expo motion-safe:[&>:first-child]:fill-mode-both',
                isAiLabsEnginePage
                  ? 'flex h-full max-h-full min-h-0 flex-col overflow-y-hidden p-0'
                  : 'min-h-0 overflow-y-visible p-(--page-gutter) [--page-gutter:--spacing(6)] md:[--page-gutter:--spacing(8)]'
              )}
            >
              {children}
              <SchedulingForm />
              <GlobalSupportDrawer />
              <GlobalHelpChat />
              <CommandPalette />
              <TourHost />
            </main>
          </div>
        </AppInset>
      </div>
    </div>
  );
}

export default AuthPageLayout;
